import * as React from "react";
import formatDate from "dateformat";
import {
  ResponsiveContainer,
  YAxis,
  XAxis,
  Line,
  Tooltip,
  ComposedChart,
  Area,
} from "recharts";
import { transparentize } from "polished";
import { TooltipContainerStyles } from "../../../../../design-tokens/charts";
import { DarkTheme } from "../../../../../design-tokens/media-queries";
import { Colors } from "../../../../../design-tokens/colors";
import { CountryData } from "./country-test-impact-chart-group";

interface DailyAndTestChartMiniProps {
  data: CountryData["data"];
  color: string;
}

const DailyAndTestChartMini: React.FC<DailyAndTestChartMiniProps> = ({
  data,
  color,
}) => {
  let topTestPerCase = 0;
  const firstDataIndex = data.findIndex(
    (d: any) => d.averageNewCases && d.averageNewCases > 20
  );
  const dataToPlot = data.slice(firstDataIndex, data.length).map((d) => {
    topTestPerCase = Math.max(d.testsPerCase || 0, topTestPerCase);
    return {
      ...d,
      testsPerCase: Math.round(d.testsPerCase || 0),
    };
  });

  const topColor =
    topTestPerCase / 100 > 1
      ? Colors.Green
      : transparentize((1 - topTestPerCase / 100) * 0.6, Colors.Green);

  const topColorPosition =
    topTestPerCase / 100 > 1 ? (topTestPerCase - 100) / topTestPerCase : 0;

  const colorId = topColorPosition.toString().replace(".", "");

  return (
    <div
      css={{
        height: 135,
        width: "100%",
        fontSize: 10,
        [DarkTheme]: { text: { fill: Colors.DarkModeWhite } },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={dataToPlot}>
          <defs>
            <linearGradient
              id={`testGradiant_${colorId}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={transparentize(0.2, topColor)} />
              <stop
                offset={`${topColorPosition * 100}%`}
                stopColor={transparentize(0.2, topColor)}
              />
              <stop
                offset="100%"
                stopColor={transparentize(0.8, Colors.Green)}
              />
            </linearGradient>
          </defs>
          <YAxis
            yAxisId="averageNewCases"
            dataKey="averageNewCases"
            domain={["dataMin", "dataMax"]}
            hide={true}
            padding={{ top: 0, bottom: 10 }}
          />
          <YAxis
            yAxisId="testsPerCase"
            dataKey="testsPerCase"
            domain={[0, 100]}
            hide={true}
          />
          <XAxis
            dataKey="date"
            height={20}
            tickLine={false}
            tickMargin={5}
            interval="preserveStartEnd"
            minTickGap={15}
            scale="point"
            tickFormatter={(date) => formatDate(date, "mmm d")}
          />

          <Area
            type="basis"
            yAxisId="testsPerCase"
            dataKey="testsPerCase"
            name="Tests/Case"
            fill={`url(#testGradiant_${colorId})`}
            stroke={`url(#testGradiant_${colorId})`}
          />

          <Line
            type="basis"
            yAxisId="averageNewCases"
            dataKey="averageNewCases"
            name="Avg new daily"
            stroke={color}
            strokeWidth={3}
            dot={false}
          />

          <Tooltip
            labelFormatter={(date) => formatDate(date, "mmm d")}
            cursor={false}
            contentStyle={TooltipContainerStyles}
            itemStyle={{ paddingBottom: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DailyAndTestChartMini;
